import type { JSX, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import EventBus from './EventBus';
import MockTransport from './MockTransport';
import type { EventMap, ITransport } from './types';

interface IRealtimeContext<TEventMap extends EventMap = EventMap> {
  /** The event bus instance for subscribing and publishing events */
  eventBus: EventBus<TEventMap>;
  /** The transport instance (for simulating events in dev mode) */
  transport: ITransport;
}

interface IRealtimeProviderProps {
  children: ReactNode;
  /**
   * The transport to use for receiving remote events.
   * Defaults to MockTransport for local-only events.
   */
  transport?: ITransport;
}

const RealtimeContext = createContext<IRealtimeContext | undefined>(undefined);

/**
 * Provider component for the realtime event system.
 *
 * Wraps the application and provides access to the event bus for
 * subscribing to and publishing events.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * import { RealtimeProvider } from '@feathr/hooks';
 *
 * function App() {
 *   return (
 *     <RealtimeProvider>
 *       <YourApp />
 *     </RealtimeProvider>
 *   );
 * }
 * ```
 */
function RealtimeProvider<TEventMap extends EventMap = EventMap>({
  children,
  transport: transportProp,
}: Readonly<IRealtimeProviderProps>): JSX.Element {
  const transportRef = useRef<ITransport>(transportProp ?? new MockTransport());
  const eventBusRef = useRef<EventBus<TEventMap>>(new EventBus<TEventMap>());

  const transport = transportRef.current;
  const eventBus = eventBusRef.current;

  useEffect(() => {
    transport.onEvent((type, payload) => {
      eventBus.publish(type as keyof TEventMap, payload as TEventMap[keyof TEventMap]);
    });

    transport.connect().catch(() => {
      /*
       * Connection failure is expected when:
       * - SSE endpoint is not available (backend not running with SSE support)
       * - Network issues
       * The transport will handle reconnection attempts internally.
       * Events can still be published locally via the debugger.
       */
    });

    return (): void => {
      transport.disconnect();
      eventBus.clear();
    };
  }, [transport, eventBus]);

  const value = useMemo(
    () => ({
      eventBus: eventBus as EventBus<EventMap>,
      transport,
    }),
    [eventBus, transport],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

/**
 * Hook to access the realtime context.
 *
 * @returns The realtime context with event bus and transport
 * @throws Error if used outside of RealtimeProvider
 */
function useRealtimeContext<TEventMap extends EventMap = EventMap>(): IRealtimeContext<TEventMap> {
  const context = useContext(RealtimeContext);

  if (context === undefined) {
    throw new Error('useRealtimeContext must be used within a RealtimeProvider');
  }

  return context as IRealtimeContext<TEventMap>;
}

export { RealtimeProvider, useRealtimeContext };
export type { IRealtimeContext, IRealtimeProviderProps };
